import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { isEmpty, mapValues } from 'lodash';
import {
  filterRecord,
  BackendResponse,
  UnitsService,
  UnitSettingsV2Service,
  DEFAULT_UNIT_SETTINGS,
} from '@schenck/rotec-components';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';

import { US_BASE_URL } from './tokens';
import {
  Parameters,
  CompareSettings,
  SearchSettings,
  ProtocolSettings,
  Settings,
  UnitSettings,
} from '../utils/interfaces/settings.interface';
import { SearchKeys } from 'src/utils/enums/search.enum';
import {
  compareSettingsDefault,
  defaultParameters,
  protocolSettingsDefault,
  searchSettingsDefaults,
  unitSettingsDefault,
} from '../utils/constants/settings.const';

@Injectable()
export class SettingsApi {
  private readonly baseUrl = inject(US_BASE_URL);
  private readonly http = inject(HttpClient);
  private readonly unitsService = inject(UnitsService);
  private readonly unitSettingService = inject(UnitSettingsV2Service);

  compareSettingsUrl: string;
  parametersUrl: string;
  searchSettingsUrl: string;
  protocolSettingsUrl: string;

  constructor() {
    const baseUrl = this.baseUrl;

    this.compareSettingsUrl = baseUrl + '/v1/currentUser/reportcenter/compare';
    this.parametersUrl = baseUrl + '/v1/currentUser/reportcenter/parameters';
    this.searchSettingsUrl = baseUrl + '/v1/currentUser/reportcenter/grid';
    this.protocolSettingsUrl = baseUrl + '/v1/currentUser/reportcenter/protocols';
  }

  /**
   * Load all user settings.
   */
  load$(): Observable<Settings> {
    return forkJoin([
      this.loadParameters$(),
      this.loadSearchSettings$(),
      this.loadProtocolSettings$(),
      this.loadCompareSettings$(),
      this.loadUnitSettings$().pipe(take(1)),
    ]).pipe(
      map(([parameters, searchSettings, protocolSettings, { showAverage }, unitSettings]) => ({
        showAverage,
        parameters,
        searchSettings,
        unitSettings,
        protocolSettings,
      }))
    );
  }

  /**
   * Persist compare settings.
   */
  setCompareSettings(settings: CompareSettings): void {
    this.http.put(this.compareSettingsUrl, settings).subscribe();
  }

  /**
   * Persist parameters settings.
   */
  setParameters(parameters: Parameters): void {
    this.http.put(this.parametersUrl, parameters).subscribe();
  }

  /**
   * Persist search settings.
   */
  setSearchSettings(settings: SearchSettings) {
    this.http.put(this.searchSettingsUrl, this.sanitizeSearchSettings(settings)).subscribe();
  }

  /**
   * Persist protocol settings.
   */
  setProtocolSettings$(
    settings: ProtocolSettings
  ): Observable<BackendResponse.Response<ProtocolSettings>> {
    return this.http
      .put<ProtocolSettings>(this.protocolSettingsUrl, settings)
      .pipe(BackendResponse.mapToResponse());
  }

  public loadCompareSettings$(): Observable<CompareSettings> {
    return this.http.get<CompareSettings>(this.compareSettingsUrl).pipe(
      map((settings) =>
        this.checkSettingsOnEmpty<CompareSettings>(settings, compareSettingsDefault)
      ),
      catchError(() => of(compareSettingsDefault))
    );
  }

  public loadUnitSettings$(): Observable<UnitSettings> {
    return combineLatest([this.unitsService.units$, this.unitSettingService.userSettings$]).pipe(
      filter(([units, settings]) => !!units && !!settings),
      map(([_, settings]) => ({
        units: {
          lengthUnit: settings.lengthValue,
          weightUnit: settings.massValue,
          unbalUnit: settings.unbalanceValue,
          rotationUnit: settings.nominalSpeedValue,
          isUnbalanceAsMass: settings.isUnbalanceAsMass,
        },
        decimals: settings.decimals != null ? settings.decimals : DEFAULT_UNIT_SETTINGS.decimals,
      })),
      catchError(() => of(unitSettingsDefault))
    );
  }

  public loadParameters$(): Observable<Parameters> {
    return this.http.get<Parameters>(this.parametersUrl).pipe(
      map((parameters) => this.checkSettingsOnEmpty<Parameters>(parameters, defaultParameters)),
      catchError(() => of(defaultParameters))
    );
  }

  public loadSearchSettings$(): Observable<SearchSettings> {
    return this.http.get<SearchSettings>(this.searchSettingsUrl).pipe(
      map((settings) =>
        this.checkSettingsOnEmpty<SearchSettings>(settings, searchSettingsDefaults)
      ),
      catchError(() => of(searchSettingsDefaults)),
      map((settings) => this.sanitizeSearchSettings(settings))
    );
  }

  public loadProtocolSettings$(): Observable<ProtocolSettings> {
    return this.http.get<ProtocolSettings>(this.protocolSettingsUrl).pipe(
      map((protocolSettings) =>
        this.checkSettingsOnEmpty<ProtocolSettings>(protocolSettings, protocolSettingsDefault)
      ),
      catchError(() => of(protocolSettingsDefault))
    );
  }

  public checkSettingsOnEmpty<T>(settings: T, defaultSettings: T): T {
    for (const key in defaultSettings) {
      if (typeof settings[key] === 'object' && (!settings[key] || isEmpty(settings[key]))) {
        settings[key] = defaultSettings[key];
      }
    }
    return settings;
  }

  /**
   * Makes sure all setting values are valid.
   */
  public sanitizeSearchSettings(settings: SearchSettings): SearchSettings {
    return {
      pageSize: settings.pageSize || 50,
      columnOrder: (settings.columnOrder || []).filter((col) => SearchKeys[col]),
      displayedColumns: (settings.displayedColumns || []).filter((col) => SearchKeys[col]),
      columnsWidths: <Record<SearchKeys, number>>mapValues(
        filterRecord(settings.columnsWidths || {}, (_, key) => SearchKeys[key]),
        (entry: string | number, key: SearchKeys) => {
          // backwards-compatibility: once it was string|number
          if (typeof entry === 'string' && entry.endsWith('%')) {
            return Number(entry.slice(0, -1));
          } else if (isNaN(<any>entry) || !entry) {
            return searchSettingsDefaults.columnsWidths[key];
          } else {
            return <number>entry;
          }
        }
      ),
    };
  }
}
